import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getGlobalApiEndpoint, getApiCompany } from "../../config/config";
import {
  Container,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Anchor,
  Alert,
  Image,
  Center,
  Box,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import rNameLogo from "../../assets/Reppi.svg";

function LoginPage() {
  const [company, setCompany] = useState(getApiCompany());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Login runs against the global host - the company is part of the payload,
      // not the URL.
      const response = await fetch(getGlobalApiEndpoint("/v2/user/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies in the request
        body: JSON.stringify({ email, password, company: company.trim() }),
      });

      await response.json();

      if (response.ok) {
        try {
          // Also global: this response's JWT is what tells us the company, so it
          // can't be routed by one. login() stores it from there.
          const tokenResponse = await fetch(
            getGlobalApiEndpoint("/v2/user/token"),
            {
              method: "GET",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (tokenResponse.ok) {
            const result = await tokenResponse.json();
            const userData = result.data;

            login(userData);

            // Check if user is verified
            if (userData.verified === false) {
              navigate("/verify-email", {
                state: { email: userData.email || email },
              });
            } else {
              const redirectTo = searchParams.get("redirect");
              navigate(redirectTo || "/training");
            }
          } else {
            setError("Failed to retrieve user data. Please try again.");
          }
        } catch (tokenError) {
          setError("Failed to retrieve user data. Please try again.");
        }
      } else {
        // Login failed
        setError("Login failed. Please check your credentials.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-primary)",
        padding: "20px",
      }}
    >
      <Container size={500}>
        <Box
          bg="var(--color-bg-card)"
          style={{
            border: "1px solid var(--color-border-extra-light)",
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "60px 40px",
          }}
        >
          <Box
            mb={40}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
            px="3rem"
          >
            <Image src={rNameLogo} alt="Reppi" h={60} w="auto" mx="auto" />
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack gap="lg">
              {error && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  color="red"
                  variant="light"
                >
                  {error}
                </Alert>
              )}

              <TextInput
                label="Company"
                placeholder="Enter your company"
                value={company}
                onChange={event => setCompany(event.currentTarget.value)}
                required
                size="md"
              />

              <TextInput
                label="Email address"
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={event => setEmail(event.currentTarget.value)}
                required
                size="md"
              />

              <PasswordInput
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={event => setPassword(event.currentTarget.value)}
                required
                size="md"
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
                loaderProps={{ type: "dots" }}
              >
                {loading ? "Logging in..." : "Log In"}
              </Button>

              <Center>
                <Anchor
                  size="sm"
                  onClick={() => navigate("/forgot-password")}
                  style={{ cursor: "pointer" }}
                >
                  Forgot Password?
                </Anchor>
              </Center>
            </Stack>
          </form>
        </Box>
      </Container>
    </Box>
  );
}

export default LoginPage;
